// ECO (economic calendar) and ERN (earnings calendar).

import { get, type Meta } from "../api";
import { button, clear, errorBox, h, loading, multiToggles, toggles } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { DataTable } from "../table";

// --- ECO --------------------------------------------------------------------

interface EcoEvent {
  t: number;
  cur: string;
  title: string;
  /// High, Medium, Low, or X for a bank holiday.
  impact: "H" | "M" | "L" | "X";
  /// Holidays: the day off (YYYY-MM-DD); `t` may fall on another local day.
  day?: string;
  fc?: string;
  prev?: string;
}

/// An event placed on the local timeline: holidays at the start of their day.
type Placed = EcoEvent & { at: number };

function place(e: EcoEvent): Placed {
  return { ...e, at: e.day ? new Date(`${e.day}T00:00`).getTime() / 1000 : e.t };
}

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "CNY", "CAD", "AUD", "NZD", "CHF"];
const IMPACT: Record<string, [string, string]> = {
  H: ["HIGH", "down"],
  M: ["MED", "amber"],
  L: ["LOW", "yellow"],
  X: ["HOLIDAY", "dim"],
};
type MinImpact = "L" | "M" | "H";
const RANK: Record<string, number> = { L: 0, M: 1, H: 2 };
const ECO_FETCH_MS = 15 * 60_000;
const ECO_TICK_MS = 30_000;

/// "in 45m", "in 3h05m", "in 2d".
function until(t: number, now: number): string {
  const m = Math.floor((t - now) / 60);
  if (m < 60) return `in ${m}m`;
  if (m < 48 * 60) return `in ${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m`;
  return `in ${Math.floor(m / 1440)}d`;
}

function runEco(ctx: Ctx): void {
  // ECO EUR GBP · ECO ALL · ECO HIGH · ECO WEEK
  const argCurs = ctx.args.filter((a) => /^[A-Z]{3}$/.test(a) && a !== "ALL" && a !== "MED" && a !== "LOW");
  let curs = new Set(ctx.args.includes("ALL") ? [] : argCurs.length ? argCurs : ["USD"]);
  let minImpact: MinImpact = ctx.args.some((a) => a === "HIGH" || a === "H") ? "H" : ctx.args.some((a) => a === "MED" || a === "M") ? "M" : "L";
  // Past days push today below the fold midweek, so they start hidden.
  let span: "today" | "week" = ctx.args.includes("WEEK") ? "week" : "today";
  let events: Placed[] = [];

  const toolbar = h("div", { class: "toolbar" });
  const list = h("div", null, loading());

  const drawToolbar = () => {
    const known = [...new Set([...CURRENCIES, ...events.map((e) => e.cur).filter((c) => c !== "ALL")])];
    clear(
      toolbar,
      ...multiToggles(known, curs, (next) => {
        curs = next;
        drawToolbar();
        render();
      }),
      h("span", { class: "sep" }),
      h("label", null, "Impact"),
      ...toggles(
        [
          ["L", "ALL"],
          ["M", "MED+"],
          ["H", "HIGH"],
        ] as [MinImpact, string][],
        minImpact,
        (v) => {
          minImpact = v;
          drawToolbar();
          render();
        },
      ),
      h("span", { class: "sep" }),
      ...toggles(
        [
          ["today", "TODAY+"],
          ["week", "WEEK"],
        ] as ["today" | "week", string][],
        span,
        (v) => {
          span = v;
          drawToolbar();
          render();
        },
      ),
    );
  };

  const render = () => {
    const now = Date.now() / 1000;
    const from = span === "week" ? 0 : new Date().setHours(0, 0, 0, 0) / 1000;
    // Global events ("ALL") and holidays show under any filter.
    const shown = events.filter(
      (e) =>
        e.at >= from &&
        (curs.size === 0 || curs.has(e.cur) || e.cur === "ALL") &&
        (e.impact === "X" || RANK[e.impact] >= RANK[minImpact]),
    );
    if (!shown.length) {
      const why = !events.length ? "No events this week." : span === "today" ? "Nothing more this week matches; WEEK shows earlier days." : "No events match the filter this week.";
      clear(list, h("div", { class: "pad dim" }, why));
      return;
    }
    const next = shown.find((e) => !e.day && e.t > now)?.t;
    const body = h("tbody");
    let lastDay = "";
    for (const e of shown) {
      const d = f.day(e.at);
      if (d !== lastDay) {
        body.append(h("tr", { class: "group" }, h("td", { colspan: "7" }, d)));
        lastDay = d;
      }
      const [impLabel, impCls] = IMPACT[e.impact] ?? IMPACT.L;
      const past = e.day ? e.at + 86400 <= now : e.t <= now;
      body.append(
        h(
          "tr",
          { class: past ? "past" : e.t === next ? "next" : undefined },
          h("td", { class: e.t === next ? "yellow" : undefined }, e.day ? "All day" : f.time(e.t)),
          h("td", { class: "amber" }, e.cur),
          h("td", { class: past ? undefined : impCls }, impLabel),
          h("td", null, e.title),
          h("td", { class: "num" }, e.fc ?? ""),
          h("td", { class: "num" }, e.prev ?? ""),
          // The countdown soaks up spare width so wide panels stay scannable.
          h("td", { class: "dim", style: { width: "100%" } }, past || e.day ? "" : until(e.t, now)),
        ),
      );
    }
    const head = h(
      "tr",
      null,
      ...["Time", "Cur", "Impact", "Event", "Forecast", "Previous", ""].map((l, i) => h("th", { class: i === 4 || i === 5 ? "num" : undefined }, l)),
    );
    const top = ctx.body.scrollTop;
    clear(list, h("table", { class: "dt" }, h("thead", null, head), body));
    ctx.body.scrollTop = top;
  };

  const load = async () => {
    let r: { data: { events: EcoEvent[] }; meta: Meta };
    try {
      r = await get<{ events: EcoEvent[] }>("calendar/eco", {}, ctx.signal);
    } catch (e) {
      if (ctx.signal.aborted) return;
      if (!events.length) clear(list, errorBox(e));
      else ctx.panel.status(`Refresh failed: ${e instanceof Error ? e.message : e}`);
      return;
    }
    if (ctx.signal.aborted) return;
    events = r.data.events.map(place).sort((a, b) => a.at - b.at);
    drawToolbar();
    render();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    ctx.panel.status(`ForexFactory · this week · times in ${tz}`, r.meta);
  };

  drawToolbar();
  clear(ctx.body, toolbar, list);
  load();
  ctx.panel.every(ECO_FETCH_MS, load);
  // Move the past/upcoming line and countdowns along without refetching.
  ctx.panel.every(ECO_TICK_MS, () => events.length && render());
}

// --- ERN --------------------------------------------------------------------

interface Earning {
  sym: string;
  name: string;
  time: "pre" | "post" | "";
  eps_fc?: number;
  ests?: number;
  ly_eps?: number;
  ly_date?: string;
  eps?: number;
  surprise?: number;
  mcap?: number;
  fq?: string;
}

const ERN_PAST = 30;
const ERN_AHEAD = 90;
const ERN_REFRESH_MS = 10 * 60_000;
const DATE_ARG = /^\d{4}-\d{2}-\d{2}$/;

/// Today in New York, where Nasdaq's dates live, as YYYY-MM-DD.
function nyToday(): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

const noon = (d: string) => new Date(`${d}T12:00:00Z`);
const addDays = (d: string, n: number) => {
  const x = noon(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const daysBetween = (a: string, b: string) => Math.round((noon(b).getTime() - noon(a).getTime()) / 86_400_000);
const isWeekend = (d: string) => [0, 6].includes(noon(d).getUTCDay());

const TIMES: Record<string, string> = { pre: "Pre-mkt", post: "After-hrs" };

function runErn(ctx: Ctx): void {
  // "TUE 20 OCT · TOMORROW", relative to New York rather than the browser.
  const dayLabel = (d: string) => {
    const local = new Date(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
    const rel = { [-1]: "YESTERDAY", 0: "TODAY", 1: "TOMORROW" }[daysBetween(nyToday(), d)];
    return [f.day(local).split(" · ")[0], rel].filter(Boolean).join(" · ");
  };
  const inRange = (d: string) => {
    const n = daysBetween(nyToday(), d);
    return n >= -ERN_PAST && n <= ERN_AHEAD;
  };
  const asked = ctx.args.find((a) => DATE_ARG.test(a) && !isNaN(noon(a).getTime()) && inRange(a));
  let date = asked ?? nyToday();
  // Nobody reports at the weekend; land on Monday instead.
  while (!asked && isWeekend(date)) date = addDays(date, 1);
  let seq = 0;

  const toolbar = h("div", { class: "toolbar" });
  const note = h("div");
  const table = new DataTable<Earning>(
    [
      { label: "Ticker", get: (r) => r.sym, cls: () => "amber" },
      { label: "Company", get: (r) => r.name, cls: () => "name" },
      { label: "Time", get: (r) => TIMES[r.time] ?? "", fmt: (v: string) => v || "—", cls: () => "dim" },
      { label: "EPS est", get: (r) => r.eps_fc, fmt: (v) => f.price(v, 2), num: true, title: "Consensus EPS forecast" },
      { label: "EPS", get: (r) => r.eps, fmt: (v) => f.price(v, 2), cls: (v: number, r) => (r.eps_fc === undefined ? "" : f.cls(v - r.eps_fc)), num: true, title: "Reported EPS" },
      { label: "Surp", get: (r) => r.surprise, fmt: (v) => f.pct(v, 1), cls: (v) => f.cls(v), num: true, title: "EPS surprise vs consensus" },
      { label: "#Est", get: (r) => r.ests, num: true, title: "Number of estimates" },
      { label: "LY EPS", get: (r) => r.ly_eps, fmt: (v) => f.price(v, 2), num: true, title: "EPS a year ago" },
      { label: "Mkt cap", get: (r) => r.mcap, fmt: (v) => f.big(v, 1), num: true },
      { label: "Qtr", get: (r) => r.fq, cls: () => "dim" },
    ],
    { panel: ctx.panel, key: (r) => r.sym, action: (r) => `${r.sym} DES` },
  );

  const go = (d: string) => {
    date = d;
    drawToolbar();
    load(true);
  };
  const step = (dir: 1 | -1) => {
    let d = addDays(date, dir);
    while (isWeekend(d)) d = addDays(d, dir);
    if (inRange(d)) go(d);
  };

  const drawToolbar = (count?: number) => {
    const prev = button("◀ Prev", () => step(-1), false, "Previous weekday");
    const next = button("Next ▶", () => step(1), false, "Next weekday");
    prev.disabled = !inRange(addDays(date, -1));
    next.disabled = !inRange(addDays(date, 1));
    clear(
      toolbar,
      prev,
      button("Today", () => go(nyToday()), date === nyToday()),
      next,
      h("span", { class: "sep" }),
      h("label", null, dayLabel(date)),
      h("span", { class: "dim" }, date),
      count !== undefined ? h("span", { class: "dim", style: { marginLeft: "auto" } }, `${count} companies`) : null,
    );
  };

  const load = async (fresh = false) => {
    const my = ++seq;
    if (fresh) {
      clear(note, loading());
      table.set([]);
    }
    try {
      const r = await get<{ date: string; rows: Earning[] }>("calendar/earnings", { date }, ctx.signal);
      if (ctx.signal.aborted || my !== seq) return;
      drawToolbar(r.data.rows.length);
      clear(note, r.data.rows.length ? null : h("div", { class: "pad dim" }, `No earnings scheduled for ${date}.`));
      table.el.style.display = r.data.rows.length ? "" : "none";
      table.set(r.data.rows);
      ctx.panel.status("Nasdaq earnings calendar · US Eastern dates", r.meta);
    } catch (e) {
      if (ctx.signal.aborted || my !== seq) return;
      table.set([]);
      table.el.style.display = "none";
      clear(note, errorBox(e), h("div", { class: "pad dim" }, "Nasdaq's API is unofficial and refuses some server addresses; try again later."));
      ctx.panel.status("Nasdaq earnings calendar");
    }
  };

  drawToolbar();
  clear(ctx.body, toolbar, note, table.el);
  load(true);
  // Today's reports fill in through the session.
  ctx.panel.every(ERN_REFRESH_MS, () => Math.abs(daysBetween(nyToday(), date)) <= 1 && load());
}

register(
  {
    code: "ECO",
    name: "Economic Calendar",
    group: "Rates & Economy",
    needsSec: false,
    help: "This week's economic releases with impact, forecast and previous. ECO EUR, ECO ALL HIGH, ECO WEEK.",
    run: runEco,
  },
  {
    code: "ERN",
    name: "Earnings Calendar",
    group: "Equity",
    needsSec: false,
    args: DATE_ARG,
    help: "Companies reporting on a day, with EPS estimates and surprises. ERN YYYY-MM-DD for a date.",
    run: runErn,
  },
);
